import clsx from 'clsx';
import {
  type L3Link,
  type ToolsResourcesSection as ToolsResourcesSectionType,
} from '../../../../services/globalNavItems/types';
import { SegmentAwareAnchor } from '../../../SegmentLink';
import { Typography } from '../../../Typography';
import { ToolsResourcesSection } from './ToolsResourcesSection';

type L3AnchorProps = {
  bolded?: boolean;
  children: string;
  href: string;
  position: number;
  l1Title: string;
  l2Title: string;
};

const L3Anchor = ({
  bolded = false,
  children,
  href,
  position,
  l1Title,
  l2Title,
}: L3AnchorProps) => (
  <SegmentAwareAnchor
    segmentAttributes={{
      entity_name: 'global_nav_l3_link',
      item_level: 3,
      item_position: position,
      item_copy: children,
      l1_nav_copy: l1Title,
      l2_nav_copy: l2Title,
      destination_url: href,
      navigation_format: 'header',
    }}
    href={`${href}`}
    className={clsx(
      'no-underline',
      'text-neutral-darkest',
      'hover:text-green-default',
      'py-2',
      'font-light',
      'text-xsmall',
      {
        'font-semibold': bolded,
      },
    )}
    aria-label={`Navigate to ${children}`}
  >
    {children}
  </SegmentAwareAnchor>
);

type L3NavigationProps = {
  isOpen: boolean;
  l3Links?: L3Link[];
  toolsResourcesSection?: ToolsResourcesSectionType;
  moreInfoLink?: {
    linkText: string;
    uri: string;
  };
  l1Title: string;
  l2Title: string;
  id: string;
};

export function L3Navigation({
  isOpen,
  l3Links,
  toolsResourcesSection,
  moreInfoLink,
  l1Title,
  l2Title,
  id,
}: L3NavigationProps) {
  const hasNavLinks = !!(l3Links?.length || moreInfoLink);

  if (!hasNavLinks && !toolsResourcesSection) {
    return null;
  }

  return (
    <div id={id} className={clsx('mb-4', 'gap-6', { flex: isOpen, hidden: !isOpen })}>
      {hasNavLinks && (
        <ul className={clsx('flex', 'flex-col', 'p-0', 'm-0')}>
          {l3Links?.map((link, index) => (
            <li key={link.id} className={clsx('flex', 'list-none')}>
              {link.itemLink ? (
                <L3Anchor
                  href={link.itemLink.uri}
                  bolded={false}
                  position={index}
                  l1Title={l1Title}
                  l2Title={l2Title}
                >
                  {link.title}
                </L3Anchor>
              ) : (
                <Typography className={clsx('text-neutral-darkest', 'py-2', 'font-light')}>
                  {link.title}
                </Typography>
              )}
            </li>
          ))}
          {moreInfoLink && (
            <li className={clsx('flex', 'list-none')}>
              <L3Anchor
                href={moreInfoLink.uri}
                bolded
                position={l3Links?.length ?? 0}
                l1Title={l1Title}
                l2Title={l2Title}
              >
                {moreInfoLink.linkText}
              </L3Anchor>
            </li>
          )}
        </ul>
      )}
      {toolsResourcesSection && (
        <ToolsResourcesSection
          section={toolsResourcesSection}
          l1Title={l1Title}
          l2Title={l2Title}
        />
      )}
    </div>
  );
}
